/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  type CreateClientInput,
  type CreateTherapistInput,
  type InviteParentInput,
  type TherapistCategory,
  type UpdateTherapistInput,
} from '@therapy/shared-validators';

import { apiClient } from './client.js';

export type { InviteParentInput };

/** Result of inviting a parent — the one-time activation link to share. */
export interface ParentInvite {
  readonly email: string;
  readonly activationUrl: string;
  readonly expiresAt: string;
}

export interface Client {
  readonly id: string;
  readonly fullName: string;
  readonly age: number | null;
  readonly guardianName: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly primaryDiagnosis: string | null;
  readonly status: 'active' | 'inactive' | 'discharged';
  readonly sessionsCount: number;
  readonly sessionsPerWeek: number | null;
  readonly progressPct: number;
  readonly familyId: string | null;
  readonly therapistName: string | null;
  readonly feePerSessionPaise: number | null;
  readonly planName: string | null;
}

/** A bounded slice of a list endpoint (P1-1). */
export interface Page<T> {
  readonly items: readonly T[];
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
}

/** Query params for the paginated client list (offset/limit + search + status). */
export interface ClientsPageParams {
  readonly limit: number;
  readonly offset: number;
  readonly q?: string | undefined;
  readonly status?: Client['status'] | undefined;
}

export interface Therapist {
  readonly id: string;
  readonly fullName: string;
  readonly email: string;
  readonly phone: string | null;
  readonly specialisations: readonly string[];
  readonly status: 'active' | 'inactive';
  readonly category: TherapistCategory;
  readonly perSessionRatePaise: number | null;
  readonly monthlyFixedPaise: number | null;
  readonly rating: number | null;
  readonly joinedOn: string | null;
  readonly clientsCount: number;
  readonly sessionsCount: number;
}

/** Query params for the paginated therapist list (offset/limit + search + status/category). */
export interface TherapistsPageParams {
  readonly limit: number;
  readonly offset: number;
  readonly q?: string | undefined;
  readonly status?: Therapist['status'] | undefined;
  readonly category?: Therapist['category'] | undefined;
}

/** Keep only non-empty values so the API receives clean, absent optionals. */
function compact(record: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(record).filter(
      ([, value]) =>
        value !== undefined && value !== '' && !(typeof value === 'number' && Number.isNaN(value)),
    ),
  );
}

function pruneClient(input: CreateClientInput): Record<string, unknown> {
  return compact({
    fullName: input.fullName,
    age: input.age,
    primaryDiagnosis: input.primaryDiagnosis,
    email: input.email,
    phone: input.phone,
    assignedTherapistId: input.assignedTherapistId,
    familyId: input.familyId,
  });
}

function pruneTherapist(
  input: CreateTherapistInput | UpdateTherapistInput,
): Record<string, unknown> {
  const specialisations =
    input.specialisations !== undefined && input.specialisations.length > 0
      ? input.specialisations
      : undefined;
  return compact({
    fullName: input.fullName,
    email: input.email,
    phone: input.phone,
    specialisations,
    joinedOn: input.joinedOn,
    category: input.category,
    perSessionRatePaise: input.perSessionRatePaise,
    monthlyFixedPaise: input.monthlyFixedPaise,
  });
}

export const clientsApi = {
  async list(): Promise<readonly Client[]> {
    const { data } = await apiClient.get<readonly Client[]>('/clients');
    return data;
  },
  async listPage(params: ClientsPageParams): Promise<Page<Client>> {
    const { data } = await apiClient.get<Page<Client>>('/clients/page', { params });
    return data;
  },
  async getOne(id: string): Promise<Client> {
    const { data } = await apiClient.get<Client>(`/clients/${id}`);
    return data;
  },
  async create(input: CreateClientInput): Promise<Client> {
    const { data } = await apiClient.post<Client>('/clients', pruneClient(input));
    return data;
  },
  async update(id: string, input: CreateClientInput): Promise<Client> {
    const { data } = await apiClient.patch<Client>(`/clients/${id}`, pruneClient(input));
    return data;
  },
  async remove(id: string): Promise<void> {
    await apiClient.delete(`/clients/${id}`);
  },
  async inviteParent(id: string, input: InviteParentInput): Promise<ParentInvite> {
    const { data } = await apiClient.post<ParentInvite>(`/clients/${id}/invite-parent`, input);
    return data;
  },
  async assignTherapist(id: string, therapistId: string): Promise<void> {
    await apiClient.post(`/clients/${id}/therapists`, { therapistId });
  },
};

export const therapistsApi = {
  async list(): Promise<readonly Therapist[]> {
    const { data } = await apiClient.get<readonly Therapist[]>('/therapists');
    return data;
  },
  async listPage(params: TherapistsPageParams): Promise<Page<Therapist>> {
    const { data } = await apiClient.get<Page<Therapist>>('/therapists/page', { params });
    return data;
  },
  async create(input: CreateTherapistInput): Promise<Therapist> {
    const { data } = await apiClient.post<Therapist>('/therapists', pruneTherapist(input));
    return data;
  },
  async update(id: string, input: UpdateTherapistInput): Promise<Therapist> {
    const { data } = await apiClient.patch<Therapist>(`/therapists/${id}`, pruneTherapist(input));
    return data;
  },
};
